import { Logger } from "@/apis/Logger";
import { getOS, OPERATING_SYSTEM } from "@/helpers/os-helper";
import { type FeatureFlags } from "@/stores/contexts/FeatureFlagsContext";

/**
 * Supported operating systems for FAQ platform
 */
type SupportedFAQPlatform = Extract<
  OPERATING_SYSTEM,
  OPERATING_SYSTEM.WINDOWS | OPERATING_SYSTEM.MAC
>;

/**
 * Defines whether a FAQ item is applicable to all systems or a specific platform
 */
export type QuestionApplicability = "universal" | SupportedFAQPlatform;

/**
 * Represents a single FAQ item with its associated metadata
 */
export interface FAQItem {
  questionId: string;
  answerId: string;
  dataTestId: string;
  questionApplicability: QuestionApplicability;
  /** When set, the item only shows if this feature flag is enabled. */
  requiresFeatureFlag?: FeatureFlags;
  /** When set, the item is hidden if this feature flag is enabled. */
  hiddenByFeatureFlag?: FeatureFlags;
}

/**
 * Response structure for FAQ data requests
 */
export interface FAQResponse {
  /** Title of the FAQ section */
  faqTitle: string;
  /** Array of FAQ items */
  faqItems: FAQItem[];
}

/**
 * Type definition for imported FAQ JSON module
 */
interface FAQModuleType {
  default: {
    faqTitle: string;
    faqItems: FAQItem[];
  };
}

/**
 * Mapping of steps that have self-help enabled for each supported platform
 */
const SELF_HELP_ENABLED_STEPS: Record<SupportedFAQPlatform, string[]> = {
  windows: ["RegisterSecurityKey", "ConnectVpn"],
  mac: ["RegisterSecurityKey", "PreCacheADMobile", "ConnectVpn"],
};

/**
 * Validates if a FAQ item is applicable for the current platform
 */
const isValidFAQItem = (item: FAQItem): boolean =>
  ["universal", getOS() as SupportedFAQPlatform].includes(
    item.questionApplicability
  );

/**
 * Helper object containing FAQ-related functionality
 */
export const faqHelper = {
  /**
   * Loads and validates FAQ data for a specific step
   * @param stepId - The identifier of the step to load FAQ data for
   * @returns Promise containing the FAQ data or error information
   *
   * Note: Using webpackMode: "eager" to:
   *  - Ensure FAQ content is bundled with main JS, preventing loading failures
   *  - Eliminate network dependencies for FAQ data
   *  - Improve reliability as FAQ content is critical for user self-help
   */
  loadFAQData: async (stepId: string): Promise<FAQResponse> => {
    try {
      const faqModule = (await import(
        /* webpackMode: "eager" */
        `@/assets/faq/faq-step-${stepId}.json`
      )) as FAQModuleType;

      if (!Array.isArray(faqModule.default.faqItems)) {
        throw new Error("Invalid FAQ data structure");
      }

      return {
        faqTitle: faqModule.default.faqTitle,
        faqItems: faqModule.default.faqItems.filter(isValidFAQItem),
      } as FAQResponse;
    } catch (error) {
      void Logger.error(
        `Error loading FAQ data for stepId ${stepId}`,
        error as Error
      );
      throw error;
    }
  },
};

/**
 * Checks if self-help is enabled for a specific step on the current platform
 * @param stepId - The identifier of the step to check
 * @returns True if self-help is enabled for the step
 */
export const isSelfHelpEnabled = (stepId: string): boolean => {
  const steps = SELF_HELP_ENABLED_STEPS[getOS() as SupportedFAQPlatform];
  return steps ? steps.includes(stepId) : false;
};

export default faqHelper;
